<template>
  <div class="card-body">
    <!-- Technical Details -->
    <div class="form-row align-items-baseline">
      <div class="col-12">
        <div class="alert alert-info">
          <i class="fas fa-info-circle"></i>
          <span>{{ trans('Please describe your vehicle using as many individual features as possible.') }}<br>
                {{ trans('These data are used to identify your vehicle in case of theft, regardless of the chassis number.')}}</span>
        </div>

        <h4>{{ trans('Technical Details') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row align-items-baseline">
      <!-- Engine Number -->
      <div class="form-group col-lg-4">
        <label for="engine_number" class="font-weight-bold text-dark text-2">{{ trans('Engine Number') }}</label>
        <input id="engine_number" type="text"
               placeholder="WDB33322224"
               v-model="engineNumberValue"
               class="form-control" :class="{ error: hasError('engine_number') }">
        <label class="error" v-if="hasError('engine_number')">{{ errors['engine_number'][0] }}</label>
      </div>

      <!-- Gearbox -->
      <div class="form-group col-lg-4">
        <label for="gearbox_number" class="font-weight-bold text-dark text-2">{{ trans('Gearbox Number') }}</label>
        <input id="gearbox_number" type="text"
               placeholder="DBNJC-D4R59"
               v-model="gearboxNumberValue"
               class="form-control" :class="{ error: hasError('gearbox_number') }">
        <label class="error" v-if="hasError('gearbox_number')">{{ errors['gearbox_number'][0] }}</label>
      </div>

      <!-- Engine Variant -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">{{ trans('Engine Variant') }}</label>
        <div class="row">
          <div class="col-6 pr-0">
            <v-select v-model="engineValue.arrangement"
                      :placeholder="trans('Engine Variant')"
                      :clearable="false"
                      :loading="!configurations || !configurations.length"
                      :disabled="!configurations || !configurations.length"
                      :reduce="option => option.value"
                      :options="configurations"
                      :class="{ error: hasError('engine.arrangement') }" />
          </div>
          <div class="col-6">
            <v-select v-model="engineValue.cylinder_count"
                      placeholder="6"
                      :clearable="false"
                      :disabled="!this.engineValue.arrangement"
                      :reduce="option => option.value"
                      :class="{ error: hasError('engine.cylinder_count') }"
                      :options="[
                        { value: 2, label: '2 ' + trans('cyl.') },
                        { value: 3, label: '3 ' + trans('cyl.') },
                        { value: 4, label: '4 ' + trans('cyl.') },
                        { value: 5, label: '5 ' + trans('cyl.') },
                        { value: 6, label: '6 ' + trans('cyl.') },
                        { value: 8, label: '8 ' + trans('cyl.') },
                        { value: 10, label: '10 ' + trans('cyl.') },
                        { value: 12, label: '12 ' + trans('cyl.') },
                      ]" />
          </div>
        </div>
        <label class="error" v-if="hasError('engine.arrangement')">{{ errors['engine.arrangement'][0] }}</label>
        <label class="error" v-if="hasError('engine.cylinder_count') && !hasError('engine.cylinder_count')">{{ errors['engine.cylinder_count'][0] }}</label>
      </div>
    </div>

    <!-- Equipment -->
    <div class="form-row align-items-baseline">
      <div class="col-12">
        <h4>{{ trans('Equipment') }}</h4>
        <hr class="solid mt-0">
      </div>
    </div>
    <div class="form-row align-items-baseline">
      <!-- Extras -->
      <div class="form-group col-lg-4">
        <label class="font-weight-bold text-dark text-2">
          {{ trans('Extras') }}
        </label>
        <div class="row" v-for="(extrasRow, rowKey) in extrasChunked" :key="rowKey">
          <div class="col-lg-6 d-flex align-items-start"
               v-for="(item, extrasKey) in extrasRow"
               :key="extrasKey">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input"
                     :id="`${id}-extra-${item.value}`"
                     v-model="extrasValue[item.value]">

              <label class="custom-control-label text-lg-1"
                     :for="`${id}-extra-${item.value}`"
              >
                {{ item.label }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group col-lg-8">
        <div class="row">

          <!-- Seat Cover Material -->
          <div class="col-lg-6">
            <div class="form-group">
              <label :for="`${id}-seat-cover`" class="font-weight-bold text-dark text-2">
                {{ trans('Seat Cover Material') }}
              </label>
              <v-select v-model="seatMaterialValue"
                        @change="$emit('update:seatMaterial', seatMaterialValue)"
                        :placeholder="trans('Seat Cover Material')"
                        :reduce="option => option.value"
                        :loading="!seatMaterials || !seatMaterials.length"
                        :disabled="!seatMaterials || !seatMaterials.length"
                        :options="seatMaterials"
                        :class="{ error: hasError('seat_material') }" />
              <label class="error" v-if="hasError('seat_material')">{{ errors['seat_material'][0] }}</label>
            </div>
          </div>

          <!-- Interior Color -->
          <div class="col-lg-6">
            <div class="form-group">
              <label :for="`${id}-interior-color`" class="font-weight-bold text-dark text-2">
                {{ trans('Interior Color') }}
              </label>
              <v-select v-model="interiorColorValue"
                        @change="$emit('update:interiorColor', interiorColorValue)"
                        :placeholder="trans('Interior Color')"
                        :reduce="option => option.value"
                        :loading="!interiorColors || !interiorColors.length"
                        :disabled="!interiorColors || !interiorColors.length"
                        :options="interiorColors"
                        :class="{ error: hasError('interior_color') }" />
              <label class="error" v-if="hasError('interior_color')">{{ errors['interior_color'][0] }}</label>
            </div>
          </div>

          <!-- Rims -->
          <div class="col-lg-6">
            <div class="form-group">
              <label :for="`${id}-rims`" class="font-weight-bold text-dark text-2">
                {{ trans('Rims') }}
              </label>
              <input :id="`${id}-rims`"
                     type="text"
                     :placeholder="trans('Alloy Rims')"
                     v-model="rimsValue"
                     class="form-control" :class="{ error: hasError('rims') }">
              <label class="error" v-if="hasError('rims')">{{ errors['rims'][0] }}</label>
            </div>
          </div>

          <!-- Rims Manufacturer -->
          <div class="col-lg-6">
            <div class="form-group">
              <label :for="`${id}-rims-manufacturer`" class="font-weight-bold text-dark text-2">
                {{ trans('Rims Manufacturer') }}
              </label>
              <input :id="`${id}-rims-manufacturer`"
                     type="text"
                     placeholder="BBS"
                     v-model="rimsManufacturerValue"
                     class="form-control" :class="{ error: hasError('rims_manufacturer') }">
              <label class="error" v-if="hasError('rims_manufacturer')">{{ errors['rims_manufacturer'][0] }}</label>
            </div>
          </div>

          <!-- Tyre Manufacturer -->
          <div class="col-12">
            <div class="form-group">
              <label :for="`${id}-tyres`" class="font-weight-bold text-dark text-2">
                {{ trans('Tyre Manufacturer') }}
              </label>
              <input :id="`${id}-tyres`"
                     type="text"
                     placeholder="Continental 175/70 R13"
                     v-model="tyresValue"
                     class="form-control" :class="{ error: hasError('tyres') }">
              <label class="error" v-if="hasError('tyres')">{{ errors['tyres'][0] }}</label>
            </div>
          </div>

          <!-- Special Signs -->
          <div class="form-group col-12">
            <label :for="`${id}-special_signs`" class="font-weight-bold text-dark text-2">
              {{ trans('Special Signs') }}
            </label>

            <custom-textarea
              :id="`${id}-special_signs`"
              :class="{ error: hasError('special_signs') }"
              v-model="specialSignsValue"
            />

            <label class="error" v-if="hasError('special_signs')">{{ errors['special_signs'] }}</label>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {apiFetchEnum} from '../../api';
  import CustomTextarea from './VehicleTheft/CustomTextarea';

  export default {
    name: "VehicleTechs",

    components: {
      CustomTextarea
    },

    props: {
      engineNumber: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      gearboxNumber: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      engine: {
        validator: prop => typeof prop === 'object' || prop === null,
        required: true,
      },
      extras: {
        validator: prop => typeof prop === 'object' || prop === null,
        required: true,
      },
      seatMaterial: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      interiorColor: {
        validator: prop => typeof prop === 'number' || prop === null,
        required: true,
      },
      rims: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      rimsManufacturer: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      tyres: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      specialSigns: {
        validator: prop => typeof prop === 'string' || prop === null,
        required: true,
      },
      errors: {
        validator: prop => typeof prop === 'object' || prop === null,
        required: true,
        default: {},
      }
    },

    data() {
      return {
        id: null,
        engineNumberValue: this.engineNumber,
        gearboxNumberValue: this.gearboxNumber,
        engineValue: this.engine,
        extrasValue: this.extras,
        seatMaterialValue: this.seatMaterial,
        interiorColorValue: this.interiorColor,
        rimsValue: this.rims,
        rimsManufacturerValue: this.rimsManufacturer,
        tyresValue: this.tyres,
        specialSignsValue: this.specialSigns,

        configurations: [],
        extrasList: [],
        seatMaterials: [],
        interiorColors: [],
      }
    },

    watch: {
      engineNumberValue(val) {
        this.$emit('update:engineNumber', val)
      },

      gearboxNumberValue(val) {
        this.$emit('update:gearboxNumber', val)
      },

      engineValue(val) {
        this.$emit('update:engine', val)
      },

      extrasValue(val) {
        const extras = [];
        val.map((val, key) => {
          if (val) {
            extras.push(key);
          }
        }).sort();
        this.$emit('update:extras', extras);
      },

      seatMaterialValue(val) {
        this.$emit('update:seatMaterial', val)
      },

      interiorColorValue(val) {
        this.$emit('update:interiorColor', val)
      },

      rimsValue(val) {
        this.$emit('update:rims', val)
      },

      rimsManufacturerValue(val) {
        this.$emit('update:rimsManufacturer', val)
      },

      tyresValue(val) {
        this.$emit('update:tyres', val)
      },

      specialSignsValue(val) {
        this.$emit('update:specialSigns', val)
      },
    },

    mounted() {
      this.id = this.$options.name + this._uid;

      Promise
        .all([
          apiFetchEnum('engine_configuration'),
          apiFetchEnum('extra'),
          apiFetchEnum('interior_material'),
          apiFetchEnum('interior_color'),
        ])
        .then(values => {
          [
            this.configurations,
            this.extrasList,
            this.seatMaterials,
            this.interiorColors,
          ] = values;
        })
        .then(() => {
          if (this.configurations && this.configurations.length) {
            this.engineValue.arrangement = this.configurations[0].value;
            this.engineValue.cylinder_count = 4;
          }
        });
    },

    computed: {
      extrasChunked() {
        return this.extrasList && this.extrasList.length > 1 ?
          this.extrasList.chunk(2) :
          [this.extrasList];
      },
    },

    methods: {
      hasError(fieldName) {
        return this.errors.hasOwnProperty(fieldName);
      }
    }
  }
</script>
